import styles from "../../styles/auditor/desktop/AuditorInquiries.module.css";
import React, {useState} from "react";
import CustomTable from "../../components/CustomTable/CustomTable";
import {useNavigate} from "react-router-dom";
import NoInquiryIcon from "../../assets/images/NoInquiryIcon.svg"
import LoadingComponent from "../../components/Loading/Loading";
import convertDateToShamsi from "../../utils/functions"
import StatusNavbar from "../../components/StatusNavbar/StatusNavbar";
import {openInquiryEvents} from "../../APIs/inquiry";
import InquiryUpdates from "../../components/InquiryUpdatesIcon/InquiryUpdates";
import useAuditorInquiries from "../../hooks/useAuditorInquiries";
import Pagination from "../../components/Pagination/Pagination";

const InquiriesDesktop = ({userRole}) => {
    const navigate = useNavigate();

    const [inquiriesPerPage] = useState(8);
    const {
        loading,
        inquiryStatus,
        setInquiryStatus,
        inquiries,
        totalInquiries,
        inquiryCounts,
        updateCounts,
        currentPage,
        setCurrentPage,
        sortConfig,
        setSortConfig,
        searchCategory,
        setSearchCategory,
        searchQuery,
        setSearchQuery,
        isSearchPerformed,
        setIsSearchPerformed,
        fetchInquiries
    } = useAuditorInquiries(userRole, inquiriesPerPage);

    const indexOfLastInquiry = currentPage * inquiriesPerPage;
    const indexOfFirstInquiry = indexOfLastInquiry - inquiriesPerPage;
    const totalPages = Math.ceil(totalInquiries / inquiriesPerPage);

    const inquiryStatuses = {
        Statuses: [
            {id: 0, status: "تمام درخواست‌ها"},
            {id: 1, status: "در انتظار پردازش"},
            {id: 2, status: "در حال بررسی"},
            {id: 3, status: "بررسی شده"},
            {id: 4, status: "خاتمه یافته"},
        ]
    };

    const handleSort = (columnIndex) => {
        const newDirection = sortConfig.key === columnIndex && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        setSortConfig({key: columnIndex, direction: newDirection});
    };

    async function handleNavigateToInquiryPage(inquiry_id) {
        try {
            if (userRole === 'auditor') {
                await openInquiryEvents(inquiry_id);
            }
            navigate(`/${userRole}/inquiry/${inquiry_id}`);
        } catch (error) {
            console.error('Error in opening inquiry event: ', error);
        }
    }

    function renderInquiryActionButton(inquiry) {
        let buttonText;
        if (userRole === 'auditor') {
            if (inquiry.status === 1) {
                buttonText = "پردازش پیش‌فاکتور‌ها";
            } else if (inquiry.status === 2) {
                buttonText = "بررسی درخواست";
            } else if ([3, 4].includes(inquiry.status)) {
                buttonText = "مشاهده درخواست";
            }
        } else {
            buttonText = "مشاهده درخواست";
        }

        return (
            <a
                href={`/${userRole}/inquiry/${inquiry.id}`}
                className={styles.detailExpandButton}
                onClick={(e) => {
                    e.preventDefault();
                    handleNavigateToInquiryPage(inquiry.id);
                }}
            >
                {buttonText}
            </a>
        );
    }

    const handleChangeInquiryStatus = (status) => {
        setInquiryStatus(status);
        setCurrentPage(1);
    };

    const handleSearchQueryChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const filterInquiries = async () => {
        setIsSearchPerformed(true);
        setCurrentPage(1);
        await fetchInquiries();
    };

    const generateTableHeaders = () => {
        const headers = [
            {value: "شناسه"},
            {value: "عنوان"},
            {value: "تاریخ", sort: true},
            {value: "ایجاد کننده", sort: true},
            {value: "نام شرکت", sort: true},
            {value: ""}
        ];

        // FIXME: The sorting is based on the header index.
        //  The issue happens because the auditor has a header for notifications,
        //  but the financial manager and manager do not.
        headers.unshift({value: "", width: userRole === "auditor" ? "40px" : "1px"});

        return [headers];
    };

    function generateTableRows() {
        if (inquiries.length !== 0) {
            return inquiries.map((inquiry) => {
                const row = [
                    {value: inquiry.name},
                    {value: inquiry.subject},
                    {value: convertDateToShamsi(inquiry.submitted_date)},
                    {value: inquiry.fullname},
                    {value: inquiry.organization},
                    {
                        value: (
                            renderInquiryActionButton(inquiry)
                        )
                    }
                ];

                row.unshift({
                    value: (
                        <InquiryUpdates
                            count={updateCounts[inquiry.id] || 0}
                            isAuditor={true}
                        />
                    ),
                    width: userRole === "auditor" ? "40px" : "1px"
                });

                return row;
            });
        } else {
            return [];
        }
    }

    const tableRows = generateTableRows();
    const tableHeaders = generateTableHeaders();

    return (
        <div
            className={styles.InquiriesContainer}>
            {userRole === 'auditor' && (
                <StatusNavbar
                    inquiryStatus={inquiryStatus}
                    handleInquirySelect={handleChangeInquiryStatus}
                    isAuditor={true}
                    inquiryCounts={inquiryCounts}
                />
            )}
            {userRole !== 'auditor' && <h3 className={styles.Header}>لیست درخواست‌ها</h3>}
            <div className={styles.SearchContainer}>
                <span className={styles.SearchText}>جستجو براساس: </span>
                <select className={styles.SearchDropDown} value={searchCategory}
                        onChange={(event) => setSearchCategory(event.target.value)}>
                    <option value="all">همه موارد</option>
                    <option value="id">شناسه درخواست</option>
                    <option value="subject">عنوان درخواست</option>
                    <option value="fullname">نام ایجاد کننده</option>
                    <option value="org">نام شرکت</option>
                    <option value="product">نام کالا</option>
                    <option value="seller">نام فروشنده خرید نهایی</option>
                </select>
                <input className={styles.SearchInput} placeholder={"عبارت مورد جستجو"} value={searchQuery}
                       onChange={handleSearchQueryChange}/>
                <button className={styles.SearchButton} onClick={() => filterInquiries()}>
                    جستجو
                </button>
            </div>

            {inquiries.length === 0 ?
                <div className={styles.EmptyInquiriesContainer}>
                    <img src={NoInquiryIcon} className={styles.EmptyInquiries} alt={"No Inquiry!"}/>
                    <span className={styles.EmptyInquiriesMessage}>
                        {inquiries.length === 0 && isSearchPerformed ?
                            (inquiryStatus === 0 ?
                                    "درخواستی مطابق با جستجوی شما در سیستم ثبت نشده است."
                                    :
                                    `درخواستی با وضعیت "${inquiryStatuses.Statuses[inquiryStatus].status}" و مطابق با جستجوی شما در سیستم ثبت نشده است.`
                            )
                            :
                            (inquiryStatus === 0 ?
                                    "درخواستی در سیستم ثبت نشده است."
                                    :
                                    `درخواستی با وضعیت "${inquiryStatuses.Statuses[inquiryStatus].status}" در سیستم ثبت نشده است.`
                            )
                        }
                    </span>
                </div>
                :
                <div className={styles.TableContainer}>
                    {loading ?
                        <LoadingComponent isMobile={false}/>
                        :
                        <div className={styles.InquiriesTableContainer}>
                            <CustomTable
                                headerData={tableHeaders}
                                data={tableRows}
                                styles={"desktop"}
                                expandedRows={[]}
                                sortConfig={sortConfig}
                                onSort={handleSort}
                            />
                        </div>
                    }
                    {!loading && inquiries.length > 0 && (
                        <div className={styles.PaginationWrapper}>
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                            <div className={styles.ItemCount}>
                                نمایش
                                موارد {indexOfFirstInquiry + 1} تا {Math.min(indexOfLastInquiry, totalInquiries)} از {totalInquiries} مورد
                            </div>
                        </div>
                    )}
                </div>
            }
        </div>
    );
};

export default InquiriesDesktop;